import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { isPopulated } from 'app/core/common/type-guards/is-populated.type';
import { FormResponsesService } from 'app/core/form-responses/form-responses.service';
import { FormResponses } from 'app/core/form-responses/form-responses.types';
import { FormService } from 'app/core/form/form.service';
import { CompanyInfosService } from 'app/core/organization/company/company-infos/company-infos.service';
import { CompanyInfos } from 'app/core/organization/company/company-infos/company-infos.types';
import { Organization } from 'app/core/organization/organization.types';
import { UserService } from 'app/core/user/user.service';
import { User, isOrganizationMember } from 'app/core/user/user.types';
import { map, of, switchMap, take } from 'rxjs';
import { isUserOnboarded } from './onboarding.guard';

export const NoOnboardingGuard: CanActivateFn | CanActivateChildFn = (
    route,
    state
) => {
    const usersService: UserService = inject(UserService);
    const authService: AuthService = inject(AuthService);
    const companyInfosService = inject(CompanyInfosService);
    const responsesService = inject(FormResponsesService);
    const formsService = inject(FormService);

    const router = inject(Router);

    return authService.check().pipe(
        take(1),
        switchMap((status: boolean) => {
            if (!status) {
                router.navigate(['/sign-in']);
                return of(false);
            }

            return usersService.user$.pipe(
                take(1),
                switchMap((user: User | null) => {
                    if (
                        user &&
                        isOrganizationMember(user) &&
                        user._organization
                    ) {
                        let orgaId: string | null = null;

                        if (isPopulated<Organization>(user._organization)) {
                            if (user._organization._id) {
                                orgaId = user._organization._id;
                            }
                        } else {
                            orgaId = user._organization;
                        }

                        if (orgaId) {
                            return companyInfosService
                                .getByCompany(orgaId)
                                .pipe(
                                    map(
                                        (
                                            companyInfos: CompanyInfos | null
                                        ): [
                                                User | null,
                                                CompanyInfos | null,
                                            ] => [user, companyInfos]
                                    )
                                );
                        }
                        return of<[User | null, CompanyInfos | null]>([
                            user,
                            null,
                        ]);
                    }
                    return of<[User | null, CompanyInfos | null]>([user, null]);
                }),
                switchMap(
                    ([user, companyInfos]: [
                        User | null,
                        CompanyInfos | null,
                    ]) =>
                        formsService
                            .find({
                                query: { tags: ['ONBOARDING_CORPO'] },
                                pagination: {
                                    page: 0,
                                    perPage: 50,
                                    sort: 'nom',
                                    order: 'asc',
                                },
                            })
                            .pipe(
                                switchMap((forms) => {
                                    if (
                                        !forms ||
                                        !forms.docs ||
                                        forms.docs.length === 0
                                    ) {
                                        return of([
                                            user,
                                            companyInfos,
                                            null,
                                        ] as [
                                                User | null,
                                                CompanyInfos | null,
                                                FormResponses | null,
                                            ]);
                                    }
                                    return responsesService
                                        .getLatest(forms.docs[0]._id!)
                                        .pipe(
                                            map(
                                                (
                                                    responses: FormResponses | null
                                                ) =>
                                                    [
                                                        user,
                                                        companyInfos,
                                                        responses,
                                                    ] as [
                                                        User | null,
                                                        CompanyInfos | null,
                                                        FormResponses | null,
                                                    ]
                                            )
                                        );
                                })
                            )
                ),
                switchMap(
                    ([user, companyInfos, responses]: [
                        User | null,
                        CompanyInfos | null,
                        FormResponses | null,
                    ]) => {
                        if (!user) {
                            router.navigate(['/sign-in']);
                            return of(false);
                        }

                        if (isUserOnboarded(user, companyInfos, responses)) {
                            router.navigate(['/dashboard']);
                            return of(false);
                        }

                        return of(true);
                    }
                )
            );
        })
    );
};
